/**
 * Reusable certification matching for job eligibility and scoring.
 * Alias-aware via resolveCanonicalCertification (EMT-B ≡ NREMT-B).
 */

import {
  certificationMatchKey,
  displayCertificationName,
  normalizeStateCode,
  normalizeStateCertificationLevel,
  profileHasCertificationForJob,
  type CertificationJobContext,
  type HeldCertification,
} from './constants/certifications';
import { jobStateCode } from './jobs/jobState';

/** Coerce Firestore / legacy values to string[] (avoids .filter on non-arrays). */
function asStringArray(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

/** Normalize job cert requirements from job doc (supports legacy top-level requiredCertifications). */
export function getJobCertRequirements(job: {
  certificationRequirements?: { requiredCertifications?: string[]; preferredCertifications?: string[]; nremtRequired?: boolean | null; stateLicenseRequired?: boolean | null } | null;
  requiredCertifications?: string[] | null;
}): { required: string[]; preferred: string[] } {
  const cr = job.certificationRequirements;
  const required = asStringArray(cr?.requiredCertifications ?? job.requiredCertifications);
  const preferred = asStringArray(cr?.preferredCertifications);
  return { required, preferred };
}

function certNameFromEntry(entry: unknown): string {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object' && entry !== null) {
    const name = (entry as { name?: unknown }).name;
    if (typeof name === 'string') return name.trim();
    if (name != null) return String(name).trim();
  }
  return String(entry).trim();
}

/** Get EMT certification names from profile (professional.certifications or legacy array). */
export function getEmtCertificationNames(profile: {
  professional?: { certifications?: Array<{ name?: unknown }> };
  certifications?: unknown[];
} | null): string[] {
  if (!profile) return [];
  const pro = profile.professional?.certifications;
  if (Array.isArray(pro) && pro.length > 0) {
    return pro.map(certNameFromEntry).filter(Boolean);
  }
  if (Array.isArray(profile.certifications)) {
    return profile.certifications.map(certNameFromEntry).filter(Boolean);
  }
  return [];
}

/**
 * Profile certifications with the issuing state (State EMS Certification entries carry a 2-letter
 * `state`). Same sources and order as getEmtCertificationNames, which stays names-only for its callers.
 */
export function getEmtCertificationEntries(profile: {
  professional?: { certifications?: Array<{ name?: unknown }> };
  certifications?: unknown[];
} | null): HeldCertification[] {
  if (!profile) return [];
  const toEntry = (entry: unknown): HeldCertification => ({
    name: certNameFromEntry(entry),
    state: entry && typeof entry === 'object' ? normalizeStateCode((entry as { state?: unknown }).state) : null,
    level: entry && typeof entry === 'object' ? normalizeStateCertificationLevel((entry as { level?: unknown }).level) : null,
  });
  const pro = profile.professional?.certifications;
  if (Array.isArray(pro) && pro.length > 0) return pro.map(toEntry).filter((e) => e.name);
  if (Array.isArray(profile.certifications)) return profile.certifications.map(toEntry).filter((e) => e.name);
  return [];
}

/**
 * The job facts credential matching needs: its state (same resolver as the Career Wallet) and whether
 * the employer requires National Registry certification. Absent fields mean today's behaviour.
 */
export function certificationJobContext(job: unknown): CertificationJobContext {
  const cr = job && typeof job === 'object' ? (job as { certificationRequirements?: { nremtRequired?: unknown; stateLicenseRequired?: unknown } | null }).certificationRequirements : null;
  return { jobState: jobStateCode(job), nremtRequired: cr?.nremtRequired === true, stateLicenseRequired: cr?.stateLicenseRequired === true };
}

export type CertificationMatchResult = {
  isEligible: boolean;
  missingRequiredCertifications: string[];
  matchedPreferredCertifications: string[];
  unmetPreferredCertifications: string[];
};

export function getCertificationMatch(params: {
  emtCertifications?: string[] | null;
  /** Entries with the issuing state (getEmtCertificationEntries). When given, used instead of emtCertifications. */
  emtCertificationEntries?: HeldCertification[] | null;
  requiredCertifications?: string[] | null;
  preferredCertifications?: string[] | null;
  /** The job's state and NREMT flag (certificationJobContext). Omitted = no state-aware rules. */
  jobContext?: CertificationJobContext | null;
}): CertificationMatchResult {
  const held: HeldCertification[] = params.emtCertificationEntries
    ? params.emtCertificationEntries.filter((e) => e?.name)
    : (params.emtCertifications ?? []).filter(Boolean).map((name) => ({ name }));
  const ctx = params.jobContext ?? null;
  const has = (cert: string) => profileHasCertificationForJob(held, cert, ctx);
  const required = (params.requiredCertifications ?? []).filter(Boolean);
  const preferred = (params.preferredCertifications ?? []).filter(Boolean);

  const missingRequiredCertifications = required
    .filter((cert) => !has(cert))
    .map(displayCertificationName);

  // Deduplicate by match key (EMT-B + NREMT-B both missing → one NREMT-B)
  const seenMissing = new Set<string>();
  const missingDeduped = missingRequiredCertifications.filter((name) => {
    const key = certificationMatchKey(name);
    if (seenMissing.has(key)) return false;
    seenMissing.add(key);
    return true;
  });

  const matchedPreferredCertifications = preferred
    .filter((cert) => has(cert))
    .map(displayCertificationName);
  const unmetPreferredCertifications = preferred
    .filter((cert) => !has(cert))
    .map(displayCertificationName);

  return {
    isEligible: missingDeduped.length === 0,
    missingRequiredCertifications: missingDeduped,
    matchedPreferredCertifications,
    unmetPreferredCertifications,
  };
}
